<template>
  <label class="input__wrap" :style="`width: ${width}`" :disabled="disabled">
    <span class="input__text" v-if="title">{{title}}{{ (required) ? '*' : '' }}</span>
    <input class="input"
           :class="classObject"
           type="text"
           :id="`flatpickr-${this._uid}`"
           ref="input"
           :disabled="disabled"
           v-model="inputValue"
           @focus="focused"
           @input="changed"
           tabindex="1"
           :placeholder="placeholder"
    >
    <span class="input__icon">
      <svg width="20" height="21" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M5 0C4.44772 0 4 0.447715 4 1V3H2C0.895431 3 0 3.89543 0 5V19C0 20.1046 0.895431 21 2 21H6.25H7.75H12.25H13.75H18C19.1046 21 20 20.1046 20 19V5C20 3.89543 19.1046 3 18 3H16V1C16 0.447715 15.5523 0 15 0C14.4477 0 14 0.447715 14 1V3H6V1C6 0.447715 5.55228 0 5 0ZM18 19.5H13.75V15.25H18.5V19C18.5 19.2761 18.2761 19.5 18 19.5ZM18.5 13.75H13.75V10.25H18.5V13.75ZM12.25 13.75V10.25H7.75V13.75H12.25ZM6.25 13.75V10.25H1.5V13.75H6.25ZM1.5 15.25H6.25V19.5H2C1.72386 19.5 1.5 19.2761 1.5 19V15.25ZM7.75 15.25H12.25V19.5H7.75V15.25ZM14 4.5H6V5C6 5.55228 5.55228 6 5 6C4.44772 6 4 5.55228 4 5V4.5H2C1.72386 4.5 1.5 4.72386 1.5 5V8.75H18.5V5C18.5 4.72386 18.2761 4.5 18 4.5H16V5C16 5.55228 15.5523 6 15 6C14.4477 6 14 5.55228 14 5V4.5Z" fill="currentColor"/>
      </svg>
    </span>

    <slot v-if="hasValidationError" />

    <transition name="scaleDown">
      <span v-if="valid.type" class="input__valid" :class="'input__valid_' + valid.type">
        {{ valid.message }}
      </span>
    </transition>
  </label>
</template>

<script>
import flatpickr from 'flatpickr'
import { Russian } from 'flatpickr/dist/l10n/ru.js'
import { formatDateTime, formatDate, formatTime } from '@/utils/utils'
import Base from '@/components/page/Form/Vinput/Base/Base.vue'

export default {
  name: 'app-input-date',
  inject: { formData: { from: 'formData', default: null } },
  extends: Base,
  props: {
    title: String,
    value: String,
    disabled: Boolean,
    required: Boolean,
    valid: {
      type: Object,
      default: () => ({})
    },
    width: {
      type: String,
      default: '100%'
    },
    type: {
      type: String,
      required: false,
      default: 'range'
    },
    timer: {
      type: Boolean,
      default: false
    },
    noCalendar: {
      type: Boolean,
      default: false
    },
    altInput: {
      type: Boolean,
      default: true
    },
    position: {
      type: String,
      default: 'auto'
    }
  },
  methods: {
    focused () {
      this.$emit('got-focus')
    },
    changed () {
      this.$emit('changed', this.$refs.input.value)
    },
    formatDateTime,
    formatDate,
    formatTime,
    setFlatpickr () {
      const inputMode = (this.$props.type !== undefined) ? this.$props.type : 'single'
      const dateFormat = this.$props.timer ? 'Y-m-d H:i' : 'Y-m-d'
      const altDateFormat = this.$props.timer ? 'd.m.Y H:i' : 'd.m.Y'
      const enableTime = this.$props.timer
      const self = this

      // на этом сепараторе завязано разбиение на одиночные даты в фильтре
      Russian.rangeSeparator = ' / '

      this.flatpickr = flatpickr(`#flatpickr-${this._uid}`, {
        locale: Russian,
        mode: `${inputMode}`,
        dateFormat: dateFormat,
        altInput: this.altInput,
        altFormat: altDateFormat,
        altInputClass: 'alt-input-date-global',
        time_24hr: enableTime,
        enableTime: enableTime,
        position: this.position,
        onOpen: function () {
          self.focused()
        }
      })
    },
    destroyFlatpickr () {
      this.flatpickr.destroy()
    }
  },
  computed: {
    inputValue: {
      get () {
        if (this.formData && this.name) {
          return this.formData[this.name] || this.defaultValue
        } else {
          return this.value || this.defaultValue
        }
      },
      set (val) {
        if (this.formData && this.name) {
          this.$set(this.formData, this.name, val)
        }
        this.$emit('value-changed', val)
      }
    },
    classObject () {
      return {
        input_error: this.valid !== {} && this.valid.type === 'error',
        input_warning: this.valid !== {} && this.valid.type === 'warning',
        input_success: this.valid !== {} && this.valid.type === 'success'
      }
    }
  },
  watch: {
    inputValue (newVal) {
      if (!newVal) {
        this.$refs.input._flatpickr.clear()
      }
      if (this.type === 'single') {
        const formatValue = this.timer ? this.noCalendar ? formatTime(newVal) : formatDateTime(newVal) : formatDate(newVal)
        this.$refs.input.nextElementSibling.value = formatValue ?? ''
      }
    },
    disabled () {
      this.destroyFlatpickr()
      setTimeout(this.setFlatpickr, 0)
    }
  },
  mounted () {
    this.setFlatpickr()
  }
}
</script>

<style lang="scss" scoped>
@import '../../../assets/css/system/_vars';
@import '../../../assets/css/system/_mixins';

.input {
  position: relative;
  z-index: 10;
  width: 100%;
  height: 40px;
  padding: 10px 15px 10px 15px;
  color: $font_color_second;
  border: 1px solid $color_third-dark;
  border-radius: 3px;
  transition: .3s;
  font: inherit;

  &__wrap {
    position: relative;
    display: block;

    &[disabled] {
      pointer-events: none;

      .input__text {
        color: $color_deactive;
      }

      .input {
        border: 1px solid $color_deactive;
      }
    }

    //@media screen and (max-width:1800px){
    //  width: 300px !important;
    //}
    //
    //@media screen and (max-width:1460px){
    //  width: 260px !important;
    //}
    //
    //@media screen and (max-width:1260px){
    //  width: 230px !important;
    //}
    //
    //@media screen and (max-width:1180px){
    //  width: 200px !important;
    //}

  }

  &__icon {
    position: absolute;
    z-index: 10;
    top: auto;
    bottom: 10px;
    left: auto;
    right: 12px;
    color: $color_second-light;
    transition: .3s;
    cursor: pointer;

    @media screen and (max-width: $tablet_start) {
      display: none;
    }
  }

  &__text {
    display: block;
    margin-bottom: 10px;
    font-weight: 600;
    color: $font_color_second;
  }

  &__valid {
    display: block;
    position: absolute;
    z-index: 9;
    top: auto;
    bottom: auto;
    transform: translateY(-5px) scaleY(1);
    transform-origin: top;
    left: 0;
    width: 100%;
    font-size: 14px;
    padding: 5px 15px 0 15px;
    color: $font_color_alt;
    border-radius: 0 0 3px 3px;
    transition: .3s;

    &__icon {
      position: absolute;
      top: calc(50% - 12.5px);
      left: auto;
      right: -35px;
      animation: fadeIn .3s linear;
    }

    &_error {
      background-color: $color_error;
    }

    &_warning {
      background-color: $color_warning;
    }

    &_success {
      background-color: $color_success;
    }
  }

  &_error {
    border: 1px solid $color_error;
  }

  &_warning {
    border: 1px solid $color_warning;
  }

  &_succes {
    border: 1px solid $color_success;
  }

  &[disabled] {
    background: $color_main;
  }

  &:hover {
    border: 1px solid $color_second-light;
  }

  &:focus {
    box-shadow: inset 0 4px 4px rgba(23, 63, 125, 0.25);
  }
}

.input-password {
  padding-right: 40px;

  &__showpass-btn {
    position: absolute;
    z-index: 99;
    bottom: 0;
    right: 0;
    background: rgba(0, 0, 0, 0);
    height: 40px;
    padding-right: 10px;
    padding-left: 10px;
    cursor: pointer;
    color: $color_deactive;
    transition: .3s;

    &:hover {
      color: $font_color_main;
    }

    &_active {
      color: $font_color_main;

      .eye-crossed-icon__cross {
        opacity: 0;
      }
    }
  }
}
</style>

<style lang="scss">
.input__wrap[disabled] .alt-input-date-global {
  border: 1px solid #c4c4c4;
}
</style>
